import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { DAY_ORDER, formatWeekDate, formatCurrencyCompact, calculateDayTotals, buildHierarchy } from '../utils/costingHelpers';

const calculateTotal = (rows) => {
    return rows.reduce((sum, row) => sum + (parseFloat(row.nett) || 0), 0);
};

const DayChips = ({ rows }) => {
    const totals = calculateDayTotals(rows);
    const activeDays = DAY_ORDER.filter(d => totals[d] > 0);
    if (activeDays.length === 0) return null;
    return (
        <div className="mobile-day-chips">
            {activeDays.map(day => (
                <span key={day} className="mobile-day-chip">
                    <span className="mobile-day-chip-label">{day.substring(0, 3)}</span>
                    <span className="mobile-day-chip-value">{formatCurrencyCompact(totals[day], { emptyDisplay: '-' })}</span>
                </span>
            ))}
        </div>
    );
};

const MobileSummaryCards = ({
    data,
    onSelectionChange,
    summarySelection: leftSelection,
}) => {
    const [expandedMap, setExpandedMap] = useState({});
    const hierarchicalData = useMemo(() => buildHierarchy(data), [data]);

    const toggleExpand = (key, e) => {
        e.stopPropagation();
        e.preventDefault();
        setExpandedMap(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const isSelected = (type, weekKey, siteName, roleName) => {
        if (!leftSelection) return false;
        if (leftSelection.type !== type) return false;
        if (leftSelection.weekKey !== weekKey) return false;
        if (type === 'week') return true;
        if (type === 'site' && leftSelection.siteName === siteName) return true;
        if (type === 'roleDept' && leftSelection.siteName === siteName && leftSelection.roleName === roleName) return true;
        return false;
    };

    if (!hierarchicalData || hierarchicalData.length === 0) {
        return <div className="mobile-empty-state">No data available</div>;
    }

    return (
        <div className="mobile-summary-cards">
            {hierarchicalData.map(week => {
                const weekKey = `week:${week.weekKey}`;
                const isWeekExpanded = expandedMap[weekKey];
                const weekTotal = calculateTotal(week.rows);
                const weekSelected = isSelected('week', week.weekKey);
                return (
                    <div key={week.weekKey} className="mobile-accordion-group">
                        <div className={`mobile-card mobile-card-period ${weekSelected ? 'mobile-card-selected' : ''}`}>
                            <button
                                className="mobile-card-expand-btn"
                                onClick={(e) => toggleExpand(weekKey, e)}
                                aria-label={isWeekExpanded ? 'Collapse' : 'Expand'}
                            >
                                {isWeekExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                            </button>
                            <div
                                className="mobile-card-body"
                                onClick={() => onSelectionChange({ type: 'week', weekKey: week.weekKey, weekEnding: week.weekEnding })}
                            >
                                <span className="mobile-card-title">W/E {formatWeekDate(week.weekEnding)}</span>
                                <span className="mobile-card-amount">{formatCurrencyCompact(weekTotal, { emptyDisplay: '-' })}</span>
                            </div>
                        </div>
                        {isWeekExpanded && week.sites.map(site => {
                            const siteKey = `site:${week.weekKey}:${site.siteName}`;
                            const isSiteExpanded = expandedMap[siteKey];
                            const siteTotal = calculateTotal(site.rows);
                            const siteSelected = isSelected('site', week.weekKey, site.siteName);
                            return (
                                <div key={siteKey} className="mobile-accordion-nested" style={{ marginLeft: '1rem' }}>
                                    <div className={`mobile-card mobile-card-week ${siteSelected ? 'mobile-card-selected' : ''}`}>
                                        <button
                                            className="mobile-card-expand-btn"
                                            onClick={(e) => toggleExpand(siteKey, e)}
                                            aria-label={isSiteExpanded ? 'Collapse' : 'Expand'}
                                        >
                                            {isSiteExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                        </button>
                                        <div
                                            className="mobile-card-body"
                                            onClick={() => onSelectionChange({
                                                type: 'site',
                                                weekKey: week.weekKey,
                                                weekEnding: week.weekEnding,
                                                siteName: site.siteName
                                            })}
                                        >
                                            <span className="mobile-card-title">{site.siteName}</span>
                                            <span className="mobile-card-amount">{formatCurrencyCompact(siteTotal, { emptyDisplay: '-' })}</span>
                                        </div>
                                    </div>
                                    {isSiteExpanded && (
                                        <>
                                            <DayChips rows={site.rows} />
                                            {site.roles.map(role => {
                                                const roleTotal = calculateTotal(role.rows);
                                                const roleSelected = isSelected('roleDept', week.weekKey, site.siteName, role.roleName);
                                                return (
                                                    <div
                                                        key={role.roleName}
                                                        className={`mobile-card mobile-card-site ${roleSelected ? 'mobile-card-selected' : ''}`}
                                                        style={{ marginLeft: '1rem' }}
                                                        onClick={() => onSelectionChange({
                                                            type: 'roleDept',
                                                            weekKey: week.weekKey,
                                                            weekEnding: week.weekEnding,
                                                            siteName: site.siteName,
                                                            roleName: role.roleName
                                                        })}
                                                    >
                                                        <div className="mobile-card-body">
                                                            <span className="mobile-card-title">{role.roleName}</span>
                                                            <span className="mobile-card-amount">{formatCurrencyCompact(roleTotal, { emptyDisplay: '-' })}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
};

MobileSummaryCards.propTypes = {
    data: PropTypes.array,
    onSelectionChange: PropTypes.func.isRequired,
    summarySelection: PropTypes.object,
};

export default MobileSummaryCards;
